import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { format, differenceInDays } from 'date-fns';
import { Package, Store, Users, ImageIcon, Pencil } from 'lucide-react';

import { Card, CardContent, CardHeader, CardTitle, CardFooter } from '@/components/atoms/card';
import { Button } from '@/components/atoms/button';
import { Separator } from '@/components/atoms/separator';
import { formatPrice } from '@/lib/utils';
import type { Currency } from '@/lib/utils/constants';
import type { AdTypeConfig } from '../../../types/ads.types';
import type { WizardData } from '../../../stores/campaign-wizard.store';
import { WalletBalanceCard } from '../shared/PricingCard';
import { NotificationPreview } from '../../preview/NotificationPreview';
import { useShops } from '@/features/shops/hooks/use-shops';
import { getImageUrl } from '@/lib/utils/image';
import {
  isNotificationType,
  isFeaturedShopType,
  isBannerType,
  calculateCampaignCost,
  getTypeConfig,
} from '../../../utils/pricing.utils';
import { CAMPAIGN_TYPE_OPTIONS } from './StepTypeSelection';

// ============ Types ============

interface StepSummaryProps {
  data: WizardData;
  currency: Currency;
  onEdit: (step: number) => void;
  typeConfigs?: AdTypeConfig[];
  audienceCount: number;
  walletBalance: number;
  isLoadingWallet: boolean;
}

// ============ Component ============

export function StepSummary({
  data,
  currency,
  onEdit,
  typeConfigs,
  audienceCount,
  walletBalance,
  isLoadingWallet,
}: StepSummaryProps) {
  const { t } = useTranslation();

  // Get shop data for notification preview
  const { data: shops = [] } = useShops();
  const currentShop = shops[0];
  const shopLogo = currentShop?.logo ? getImageUrl(currentShop.logo) : undefined;
  const shopName = currentShop?.name || 'Ma Boutique';

  const typeOption = CAMPAIGN_TYPE_OPTIONS.find((t) => t.value === data.type);
  const typeConfig = data.type ? getTypeConfig(data.type, typeConfigs) : null;
  const isNotification = data.type ? isNotificationType(data.type) : false;
  const isFeaturedShop = data.type ? isFeaturedShopType(data.type) : false;
  const hasBanner = data.type ? isBannerType(data.type) : false;

  if (!typeOption || !data.type) return null;

  // Get price per thousand for notifications
  const pricePerThousand = typeConfig
    ? currency === 'EUR' ? typeConfig.priceEur
      : currency === 'USD' ? typeConfig.priceUsd
      : typeConfig.priceKmf
    : 0;

  // Calculate final cost
  const { calculatedCost } = calculateCampaignCost(
    data.type,
    typeConfig,
    currency,
    {
      audienceCount,
      startDate: data.startDate,
      endDate: data.endDate,
      budget: data.budget,
    }
  );

  const finalCost = calculatedCost ?? data.budget;

  // Calculate days
  const days = React.useMemo(() => {
    if (!data.startDate || !data.endDate) return 0;
    return differenceInDays(new Date(data.endDate), new Date(data.startDate)) + 1;
  }, [data.startDate, data.endDate]);

  // Get preview image for notifications
  const getPreviewImage = (): string | undefined => {
    if (data.products.length > 0 && data.products[0].image) {
      return data.products[0].image;
    }
    return shopLogo;
  };

  // Get redirect description
  const getRedirectDescription = (): string => {
    if (data.products.length === 1) {
      return `le produit "${data.products[0].name}"`;
    }
    if (data.products.length > 1) {
      return 'votre boutique';
    }
    return 'votre boutique';
  };

  // Get audience label
  const getAudienceLabel = (): string => {
    if (data.type === 'push_shop_subscribers') return 'abonnés';
    if (data.type === 'push_product_likers' || data.type === 'push_single_product_likers') return 'fans';
    return 'personnes';
  };

  return (
    <div className="space-y-4 sm:space-y-6">
      <div className="text-center">
        <h2 className="text-lg sm:text-xl font-semibold mb-1 sm:mb-2">
          {t('ads.wizard.step4.title', 'Résumé de votre campagne')}
        </h2>
        <p className="text-sm text-muted-foreground hidden sm:block">
          {t('ads.wizard.step4.subtitle', 'Vérifiez les informations avant de lancer')}
        </p>
      </div>

      <div className="space-y-4 sm:space-y-5">
        {/* ========== SECTION 1: PREVIEW ========== */}
        {isNotification && (
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                📱 Aperçu de la notification
              </CardTitle>
            </CardHeader>
            <CardContent className="flex justify-center pb-3">
              <NotificationPreview
                shopName={shopName}
                shopLogo={shopLogo}
                title={data.title}
                message={data.message}
                image={getPreviewImage()}
                products={data.products.length > 1 ? data.products : undefined}
              />
            </CardContent>
            <CardFooter className="justify-center pt-0 pb-4">
              <p className="text-xs text-center text-muted-foreground">
                👆 Au clic, redirection vers {getRedirectDescription()}
              </p>
            </CardFooter>
          </Card>
        )}

        {/* Banner preview (for carousel/banner types) */}
        {hasBanner && data.bannerImagePreview && (
          <Card>
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-base">📱 Aperçu de la bannière</CardTitle>
                <Button variant="ghost" size="sm" className="text-xs h-8 px-2" onClick={() => onEdit(1)}>
                  <Pencil className="size-3 mr-1" />
                  Modifier
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-3">
                <img
                  src={data.bannerImagePreview}
                  alt="Bannière"
                  className="w-32 h-20 rounded-lg object-cover shrink-0"
                />
                <div className="text-sm text-muted-foreground">
                  {data.headline && (
                    <p className="text-foreground font-medium mb-1">« {data.headline} »</p>
                  )}
                  <p className="flex items-center gap-1">
                    <ImageIcon className="size-3" />
                    {data.bannerAction.type === 'product'
                      ? 'Redirection vers un produit'
                      : 'Redirection vers la boutique'}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* ========== SECTION 2: SUMMARY ========== */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              📋 Récapitulatif
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {/* Type row */}
            <div className="flex items-center justify-between py-2 border-b">
              <span className="text-muted-foreground">Type de campagne</span>
              <div className="flex items-center gap-2">
                <span className="font-medium text-sm">{typeOption.label}</span>
                <Button variant="ghost" size="sm" className="h-6 px-1.5" onClick={() => onEdit(0)}>
                  <Pencil className="size-3" />
                </Button>
              </div>
            </div>

            {/* Message row (notifications) */}
            {isNotification && data.message && (
              <div className="flex items-start justify-between py-2 border-b gap-2">
                <span className="text-muted-foreground shrink-0">Message</span>
                <div className="flex items-start gap-2">
                  <span className="text-sm text-right line-clamp-2">{data.message}</span>
                  <Button variant="ghost" size="sm" className="h-6 px-1.5 shrink-0" onClick={() => onEdit(1)}>
                    <Pencil className="size-3" />
                  </Button>
                </div>
              </div>
            )}

            {/* Products row (non-banner) */}
            {!hasBanner && data.products.length > 0 && (
              <div className="flex items-center justify-between py-2 border-b">
                <span className="text-muted-foreground">Produits</span>
                <div className="flex items-center gap-2">
                  <div className="flex -space-x-2">
                    {data.products.slice(0, 3).map((product) => (
                      product.image ? (
                        <img
                          key={product.refId}
                          src={product.image}
                          alt={product.name}
                          className="size-6 rounded border-2 border-background object-cover"
                        />
                      ) : (
                        <div key={product.refId} className="size-6 rounded border-2 border-background bg-muted flex items-center justify-center">
                          <Package className="size-3 text-muted-foreground" />
                        </div>
                      )
                    ))}
                  </div>
                  <span className="text-sm font-medium">{data.products.length} produit{data.products.length > 1 ? 's' : ''}</span>
                  <Button variant="ghost" size="sm" className="h-6 px-1.5" onClick={() => onEdit(1)}>
                    <Pencil className="size-3" />
                  </Button>
                </div>
              </div>
            )}

            {/* Shop row (featured_shop) */}
            {isFeaturedShop && data.selectedShop && (
              <div className="flex items-center justify-between py-2 border-b">
                <span className="text-muted-foreground">Boutique</span>
                <div className="flex items-center gap-2">
                  {data.selectedShop.logo ? (
                    <img src={data.selectedShop.logo} alt={data.selectedShop.name} className="size-6 rounded object-cover" />
                  ) : (
                    <div className="size-6 rounded bg-muted flex items-center justify-center">
                      <Store className="size-3 text-muted-foreground" />
                    </div>
                  )}
                  <span className="text-sm font-medium">{data.selectedShop.name}</span>
                  <Button variant="ghost" size="sm" className="h-6 px-1.5" onClick={() => onEdit(1)}>
                    <Pencil className="size-3" />
                  </Button>
                </div>
              </div>
            )}

            {/* Audience row (notifications) */}
            {isNotification && (
              <div className="flex items-center justify-between py-2 border-b">
                <span className="text-muted-foreground">Audience</span>
                <div className="flex items-center gap-2">
                  <Users className="size-4 text-muted-foreground" />
                  <span className="font-medium text-sm">
                    {audienceCount.toLocaleString()} {getAudienceLabel()}
                  </span>
                </div>
              </div>
            )}

            {/* Date row */}
            <div className="flex items-center justify-between py-2">
              <span className="text-muted-foreground">
                {isNotification ? "Date d'envoi" : 'Période'}
              </span>
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium">
                  {isNotification
                    ? data.startDate
                      ? format(new Date(data.startDate), 'dd/MM/yyyy')
                      : '-'
                    : data.startDate && data.endDate
                    ? `${format(new Date(data.startDate), 'dd/MM')} - ${format(new Date(data.endDate), 'dd/MM/yyyy')}`
                    : '-'}
                </span>
                <Button variant="ghost" size="sm" className="h-6 px-1.5" onClick={() => onEdit(2)}>
                  <Pencil className="size-3" />
                </Button>
              </div>
            </div>

            {/* Duration row (non-notifications) */}
            {!isNotification && days > 0 && (
              <div className="flex items-center justify-between py-2 border-t">
                <span className="text-muted-foreground">Durée</span>
                <span className="text-sm font-medium">{days} jours</span>
              </div>
            )}

            {/* Bid amount (if applicable) */}
            {data.bidAmount && (
              <div className="flex items-center justify-between py-2 border-t">
                <span className="text-muted-foreground">Enchère/clic</span>
                <span className="text-sm font-medium">{formatPrice(data.bidAmount, currency)}</span>
              </div>
            )}

            {/* Daily budget (if applicable) */}
            {data.dailyBudget && (
              <div className="flex items-center justify-between py-2 border-t">
                <span className="text-muted-foreground">Limite/jour</span>
                <span className="text-sm font-medium">{formatPrice(data.dailyBudget, currency)}</span>
              </div>
            )}
          </CardContent>
        </Card>

        {/* ========== SECTION 3: BILLING ========== */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              💳 Facturation
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Cost breakdown */}
            <div className="space-y-2">
              {isNotification ? (
                <>
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">{audienceCount.toLocaleString()} notifications</span>
                    <span>× {formatPrice(pricePerThousand, currency)}/1000</span>
                  </div>
                  <Separator />
                  <div className="flex justify-between font-semibold">
                    <span>Coût de la campagne</span>
                    <span className="text-lg text-primary">{formatPrice(finalCost, currency)}</span>
                  </div>
                </>
              ) : (
                <>
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Budget maximum</span>
                    <span>{formatPrice(data.budget, currency)}</span>
                  </div>
                  <Separator />
                  <div className="flex justify-between font-semibold">
                    <span>Maximum à payer</span>
                    <span className="text-lg text-primary">{formatPrice(finalCost, currency)}</span>
                  </div>
                </>
              )}
            </div>

            <Separator />

            {/* Wallet balance breakdown */}
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="flex items-center gap-1">
                  💰 Solde actuel
                </span>
                <span>{formatPrice(walletBalance, currency)}</span>
              </div>
              <div className="flex justify-between">
                <span className="flex items-center gap-1">
                  ➖ Coût campagne
                </span>
                <span>-{formatPrice(finalCost, currency)}</span>
              </div>
              <Separator />
              <div className="flex justify-between font-semibold">
                <span>💰 Solde après</span>
                <span className={walletBalance - finalCost < 0 ? 'text-destructive' : 'text-green-600'}>
                  {formatPrice(walletBalance - finalCost, currency)}
                </span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Wallet balance status */}
        <WalletBalanceCard
          balance={walletBalance}
          requiredAmount={finalCost}
          currency={currency}
          isLoading={isLoadingWallet}
        />
      </div>
    </div>
  );
}
